import { ComponentType } from 'react';
import { Entity } from '@backstage/catalog-model';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AppsIcon from '@material-ui/icons/Apps';
import BarChartIcon from '@material-ui/icons/BarChart';
import CategoryIcon from '@material-ui/icons/Category';
import CodeIcon from '@material-ui/icons/Code';
import DescriptionIcon from '@material-ui/icons/Description';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import ExtensionIcon from '@material-ui/icons/Extension';
import GroupIcon from '@material-ui/icons/Group';
import LanguageIcon from '@material-ui/icons/Language';
import LayersIcon from '@material-ui/icons/Layers';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import MemoryIcon from '@material-ui/icons/Memory';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import StorageIcon from '@material-ui/icons/Storage';
import WidgetsIcon from '@material-ui/icons/Widgets';
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet';
import InboxIcon from '@material-ui/icons/Inbox';
import TimelineIcon from '@material-ui/icons/Timeline';

const TYPE_ICONS: Record<string, ComponentType<any>> = {
  service: DeviceHubIcon,
  library: InboxIcon,
  website: LanguageIcon,
  frontend: LanguageIcon,
  mobile: PhoneIphoneIcon,
  documentation: DescriptionIcon,
  docs: DescriptionIcon,
  workflow: AccountTreeIcon,
  automation: AccountTreeIcon,
  pipeline: TimelineIcon,
  analytics: BarChartIcon,
  database: StorageIcon,
  openapi: SettingsEthernetIcon,
  asyncapi: SettingsEthernetIcon,
  graphql: SettingsEthernetIcon,
  grpc: SettingsEthernetIcon,
  ml: MemoryIcon,
  plugin: ExtensionIcon,
};

const KIND_ICONS: Record<string, ComponentType<any>> = {
  component: WidgetsIcon,
  api: CodeIcon,
  system: LayersIcon,
  domain: AppsIcon,
  resource: StorageIcon,
  group: GroupIcon,
  user: PersonIcon,
  template: CategoryIcon,
  location: LocationOnIcon,
};

export function entityIcon(entity: Entity): ComponentType<any> {
  const type = String((entity.spec as any)?.type ?? '').toLocaleLowerCase('en-US');
  const name = entity.metadata.name.toLocaleLowerCase('en-US');
  if (TYPE_ICONS[type]) return TYPE_ICONS[type];
  if (name.includes('analytic') || name.includes('metric')) return BarChartIcon;
  return KIND_ICONS[entity.kind.toLocaleLowerCase('en-US')] ?? WidgetsIcon;
}

export function kindIcon(kind: string): ComponentType<any> {
  return KIND_ICONS[kind.toLocaleLowerCase('en-US')] ?? WidgetsIcon;
}

const KIND_LABELS: Record<string, { singular: string; plural: string; title: string }> = {
  component: { singular: 'componente', plural: 'componentes', title: 'Components' },
  api: { singular: 'API', plural: 'APIs', title: 'APIs' },
  system: { singular: 'sistema', plural: 'sistemas', title: 'Systems' },
  domain: { singular: 'dominio', plural: 'dominios', title: 'Domains' },
  resource: { singular: 'recurso', plural: 'recursos', title: 'Resources' },
  group: { singular: 'grupo', plural: 'grupos', title: 'Groups' },
  user: { singular: 'usuario', plural: 'usuarios', title: 'Users' },
  template: { singular: 'plantilla', plural: 'plantillas', title: 'Templates' },
  location: { singular: 'location', plural: 'locations', title: 'Locations' },
};

export function kindLabels(kind: string) {
  const key = kind.toLocaleLowerCase('en-US');
  if (KIND_LABELS[key]) return KIND_LABELS[key];
  const title = key.charAt(0).toUpperCase() + key.slice(1);
  return { singular: key, plural: `${key}s`, title: `${title}s` };
}

export function lifecycleTone(
  lifecycle?: string,
): 'success' | 'warning' | 'danger' | 'default' {
  switch ((lifecycle ?? '').toLocaleLowerCase('en-US')) {
    case 'production':
      return 'success';
    case 'experimental':
    case 'beta':
    case 'development':
      return 'warning';
    case 'deprecated':
      return 'danger';
    default:
      return 'default';
  }
}

export function shortRef(ref: string): string {
  const slash = ref.indexOf('/');
  return slash >= 0 ? ref.slice(slash + 1) : ref.replace(/^[^:]+:/, '');
}

export function humanizeName(name: string): string {
  return name
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
